import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { NotificationsService } from 'angular2-notifications';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { formatDate, NgClass } from '@angular/common';
import { BsModalService } from 'ngx-bootstrap/modal';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { TicketDetailsComponent } from 'src/app/ticket-details/ticket-details.component';
import { ReminderModalComponent } from '../../../../shared/modal-windows/reminder-modal/reminder-modal.component';
import { CommonDataService } from 'src/sb-tools/typescript/services/common-data.service';
import { QuillHelperService } from 'src/sb-tools/typescript/services/quill/quill-helper.service';
import { NoteService } from 'src/app/core/services/note.service';
import { ForwardMessageService } from 'src/app/core/services/forward-message.service';
import { TicketService } from 'src/app/core/services/ticket/ticket.service';
import { ThreadElementDTO } from '@sb-tools/typescript/models/dto/thread-element.dto';
import { MessageDTO } from '@sb-tools/typescript/models/dto/message.dto';
import { TicketReminderDTO } from '@sb-tools/typescript/models/dto/ticket-reminder.dto';
import { FieldType } from '@sb-tools/typescript/enums/field-type.enum';
import { MessageThreadElementType } from '@sb-tools/typescript/enums/message-thread-element-type.enum';
import { ForwardEntityTypeEnum } from 'src/app/core/enums/forward-entity-type.enum';
import { RightPanelType } from 'src/app/core/enums/right-panel-type.enum';
import { IsReplyMessageEmptyPipe } from '../../../../shared/pipes/is-reply-message-empty.pipe';
import { ReactionTooltipTextType } from 'src/app/core/enums/reaction-tooltip-text-type.enum';
import { DetailedTicketDTO } from '../../../../core/models/dto/detailed-ticket.dto';
import { isInboxElementVisible } from '../../../../core/utils/is-inbox-element-visible.util';
import { TooltipDirective, TooltipModule } from 'ngx-bootstrap/tooltip';
import { NoteLinkLineComponent } from '../../note-link-line/note-link-line.component';
import { ReplyReminderAlertComponent } from '../../../../shared/components/reply-reminder-alert/reply-reminder-alert.component';
import { UserAvatarComponent } from '../../../../../sb-modules/Frontend-User-Avatar-Module/user-avatar.component';
import { InlineImagePreviewDirective } from '../../../../../sb-modules/Frontend-Document-Viewer-Module/directives/inline-image-preview.directive';
import { InlineImageLimitWidthDirective } from '../../../../shared/directives/inline-image-limit-width.directive';
import { ReactionsComponent } from '../../ticket-thread-social-media/reactions/reactions.component';
import { InlineSVGComponent } from '../../../../../sb-modules/Frontend-Inline-SVG-Module/inline-svg.component';
import { ReplyChatMenuComponent } from '../reply-chat-menu/reply-chat-menu.component';
import { FileViewComponent } from '../../../../../sb-modules/Frontend-Document-Viewer-Module/components/file-view/file-view.component';
import { FilePreviewComponent } from '../../../../../sb-modules/Frontend-Document-Viewer-Module/components/file-preview/file-preview.component';
import { TagsComponent } from '../../../tags/tags.component';
import { MsSentimentComponent } from '../../ms-sentiment/ms-sentiment.component';
import { ThreadSurveyResultComponent } from '../../thread-survey-result/thread-survey-result.component';
// import { SessionAuthenticationEventComponent } from '../../session-authentication-event/session-authentication-event.component';
import { ReplaceEmojisPipe } from '../../../../../sb-tools/typescript/pipes/replace-emoji/replace-emojis.pipe';
import { SafeHtmlPipe } from '../../../../../sb-tools/typescript/pipes/safe-html.pipe';
import { NewLinePipe } from '../../../../../sb-tools/typescript/pipes/new-line.pipe';
import { PrepareMessagePipe } from '../../../../../sb-tools/typescript/pipes/ticket/prepare-message.pipe';
import { TimeAgoPipe } from '../../../../shared/pipes/time-ago.pipe';
import { FilterContentAttachmentsPipe } from '../../../../shared/pipes/filter-content-attachments.pipe';

@Component({
    selector: 'app-reply-chat',
    templateUrl: './reply-chat.component.html',
    styleUrls: ['./reply-chat.component.scss'],
    imports: [
        NoteLinkLineComponent,
        ReplyReminderAlertComponent,
        NgClass,
        UserAvatarComponent,
        InlineImagePreviewDirective,
        InlineImageLimitWidthDirective,
        TranslateModule,
        ReactionsComponent,
        InlineSVGComponent,
        ReplyChatMenuComponent,
        FileViewComponent,
        FilePreviewComponent,
        TooltipModule,
        TagsComponent,
        MsSentimentComponent,
        ThreadSurveyResultComponent,
        // SessionAuthenticationEventComponent,
        ReplaceEmojisPipe,
        SafeHtmlPipe,
        NewLinePipe,
        PrepareMessagePipe,
        TimeAgoPipe,
        FilterContentAttachmentsPipe,
    ],
})
export class ReplyChatComponent implements OnInit, OnDestroy {
    @Input() parent: TicketDetailsComponent;
    @Input() reply: ThreadElementDTO;
    @Input() ticket: DetailedTicketDTO;

    FieldType = FieldType;
    forwardEntityTypeEnum = ForwardEntityTypeEnum;
    readonly isInboxElementVisible = isInboxElementVisible;
    isReplyMessageEmpty = false;
    messageThreadElementTypeEnum = MessageThreadElementType;
    reactionTooltipTextType = ReactionTooltipTextType;
    replyCreationTimeTooltipText: string;
    rightPanelTypeEnum = RightPanelType;

    private tooltipTimer;
    private unsubscriber = new Subject<void>();

    constructor(
        public commonDataService: CommonDataService,
        public quillHelperService: QuillHelperService,
        public noteService: NoteService,
        public isReplyMessageEmptyPipe: IsReplyMessageEmptyPipe,
        public forwardMessageService: ForwardMessageService,
        private bsModalService: BsModalService,
        private ticketService: TicketService,
        private notificationsService: NotificationsService,
        private translateService: TranslateService,
    ) {}

    ngOnInit() {
        if (this.reply?.message) {
            this.checkIsMessageEmpty(this.reply.message);
        }
        if (this.reply?.creationTime) {
            this.replyCreationTimeTooltipText = formatDate(this.reply.creationTime, 'dd.MM.yyyy HH:mm', 'en-US');
        }
    }

    checkIsMessageEmpty(message: MessageDTO): void {
        this.isReplyMessageEmpty = this.isReplyMessageEmptyPipe.transform(message);
    }

    hideTimeTooltip(tooltip: TooltipDirective): void {
        clearTimeout(this.tooltipTimer);
        tooltip.hide();
    }

    onRemindClick(minutes: number): void {
        const dateTime = new Date(new Date().getTime() + 60000 * minutes);

        const reminder: TicketReminderDTO = {
            replyId: this.reply.id,
            time: dateTime,
        };

        this.ticketService
            .addReminder(this.ticket.id, reminder)
            .pipe(takeUntil(this.unsubscriber))
            .subscribe((addedReminder: TicketReminderDTO) => {
                this.reply.reminder = addedReminder;
                this.parent.ticketUpdateStamp = new Date().getTime();
                this.notificationsService.success(
                    this.translateService.instant('Success!'),
                    this.translateService.instant('Reminder was successfully added.'),
                );
            });
    }

    onRemoveReminderClick(): void {
        this.ticketService
            .deleteReminder(this.ticket.id, this.reply.id)
            .pipe(takeUntil(this.unsubscriber))
            .subscribe(() => {
                this.reply.reminder = null;
                this.parent.ticketUpdateStamp = new Date().getTime();
                this.notificationsService.success(
                    this.translateService.instant('Success!'),
                    this.translateService.instant('Reminder was successfully removed.'),
                );
            });
    }

    openReminderModal(): void {
        const initialState = {
            ticketId: this.ticket.id,
            replyId: this.reply.id,
            hasReopenOption: false,
            confirmCallback: (reminder: TicketReminderDTO) => {
                this.reply.reminder = reminder;
                this.parent.ticketUpdateStamp = new Date().getTime();
            },
        };

        this.bsModalService.show(ReminderModalComponent, {
            initialState,
            ignoreBackdropClick: true,
        });
    }

    showTimeTooltip(tooltip: TooltipDirective) {
        this.tooltipTimer = setTimeout(() => {
            tooltip.show();
        }, 100);
    }

    ngOnDestroy() {
        this.unsubscriber.next();
        this.unsubscriber.complete();
    }
}
